import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * KenoConfigsAdminTab
 * -------------------
 * Multi-variant Keno config editor. Loads the DB-merged config for every
 * registered Keno variant, lets the admin edit bet levels, min/max bet,
 * paytable multipliers, progressive parameters, free-play awards, and
 * reveal timings, then PATCHes the diff. Reset restores the hardcoded
 * defaults for that variant.
 */
export const KenoConfigsAdminTab = () => {
    const [configs, setConfigs] = useState([]);
    const [selected, setSelected] = useState(null); // variant_key
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/keno-configs`, { withCredentials: true });
            setConfigs(data.configs || []);
            if (!selected && data.configs?.length) setSelected(data.configs[0].variant_key);
        } catch (e) {
            toast.error('Failed to load Keno configs');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!selected) return;
        const cfg = configs.find(c => c.variant_key === selected);
        if (cfg) setDraft(JSON.parse(JSON.stringify(cfg)));
    }, [selected, configs]);

    const patch = (updater) => setDraft(d => {
        const next = JSON.parse(JSON.stringify(d));
        updater(next);
        return next;
    });

    const save = async () => {
        if (!draft) return;
        setSaving(true);
        try {
            const body = {
                display_name: draft.display_name,
                enabled: draft.enabled,
                bet_levels: draft.bet_levels,
                min_bet: draft.min_bet,
                max_bet: draft.max_bet,
                free_play_award: draft.free_play_award,
                retrigger_award: draft.retrigger_award,
                paytable: draft.paytable,
                progressive: draft.progressive,
                timings: draft.timings,
            };
            await axios.patch(`${API}/admin/keno-configs/${draft.variant_key}`, body, { withCredentials: true });
            toast.success('Saved. Live changes will take effect on the next round.');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    const resetVariant = async () => {
        if (!draft) return;
        if (!window.confirm(`Reset "${draft.display_name}" to hardcoded defaults? This wipes any admin overrides.`)) return;
        try {
            await axios.post(`${API}/admin/keno-configs/${draft.variant_key}/reset`, {}, { withCredentials: true });
            toast.success('Reset complete');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        }
    };

    const spots = useMemo(() => [2, 3, 4, 5, 6, 7, 8, 9, 10], []);
    const hitsRows = useMemo(() => [2, 3, 4, 5, 6, 7, 8, 9, 10], []);

    if (loading) return <div className="text-white p-4">Loading Keno configs…</div>;
    if (!draft) return <div className="text-white p-4">No variant selected.</div>;

    return (
        <div className="p-4 text-white space-y-4" data-testid="keno-configs-admin">
            <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-bold tracking-wider text-yellow-400">VARIANT</label>
                <select
                    className="bg-black/60 border border-yellow-500/40 rounded px-3 py-2 text-white"
                    value={selected || ''}
                    onChange={e => setSelected(e.target.value)}
                    data-testid="keno-configs-variant-select"
                >
                    {configs.map(c => (
                        <option key={c.variant_key} value={c.variant_key}>{c.display_name}</option>
                    ))}
                </select>
                <label className="ml-auto flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!draft.enabled}
                        onChange={e => patch(d => { d.enabled = e.target.checked; })}
                        data-testid="keno-configs-enabled"
                    />
                    Enabled
                </label>
                <button
                    onClick={save}
                    disabled={saving}
                    className="px-4 py-2 rounded bg-yellow-500 text-black font-black disabled:opacity-40"
                    data-testid="keno-configs-save"
                >{saving ? 'Saving…' : 'Save Changes'}</button>
                <button
                    onClick={resetVariant}
                    className="px-4 py-2 rounded bg-red-600 text-white font-black"
                    data-testid="keno-configs-reset"
                >Reset to Defaults</button>
            </div>

            {/* --- Basics --- */}
            <fieldset className="border border-white/10 rounded-lg p-3 space-y-3">
                <legend className="text-xs font-black tracking-widest text-yellow-400 px-2">BASICS</legend>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Display Name</span>
                        <input
                            type="text"
                            value={draft.display_name || ''}
                            onChange={e => patch(d => { d.display_name = e.target.value; })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                            data-testid="keno-configs-display-name"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Min Bet ($)</span>
                        <input
                            type="number"
                            step="0.05"
                            value={draft.min_bet ?? 0}
                            onChange={e => patch(d => { d.min_bet = parseFloat(e.target.value) || 0; })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Max Bet ($)</span>
                        <input
                            type="number"
                            step="0.25"
                            value={draft.max_bet ?? 0}
                            onChange={e => patch(d => { d.max_bet = parseFloat(e.target.value) || 0; })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Bet Levels (comma-sep)</span>
                        <input
                            type="text"
                            value={(draft.bet_levels || []).join(', ')}
                            onChange={e => patch(d => {
                                d.bet_levels = e.target.value
                                    .split(',')
                                    .map(v => parseFloat(v.trim()))
                                    .filter(v => !isNaN(v) && v > 0);
                            })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                            placeholder="0.25, 0.5, 1, 2, 5"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Free Play Award</span>
                        <input
                            type="number"
                            value={draft.free_play_award ?? 0}
                            onChange={e => patch(d => { d.free_play_award = parseInt(e.target.value) || 0; })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="block text-white/70 mb-1">Retrigger Award</span>
                        <input
                            type="number"
                            value={draft.retrigger_award ?? 0}
                            onChange={e => patch(d => { d.retrigger_award = parseInt(e.target.value) || 0; })}
                            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                        />
                    </label>
                </div>
            </fieldset>

            {/* --- Progressive --- */}
            <fieldset className="border border-white/10 rounded-lg p-3 space-y-3">
                <legend className="text-xs font-black tracking-widest text-yellow-400 px-2">PROGRESSIVE JACKPOT</legend>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <label className="text-sm flex items-center gap-2 col-span-2 md:col-span-1">
                        <input
                            type="checkbox"
                            checked={!!draft.progressive?.enabled}
                            onChange={e => patch(d => { d.progressive = { ...(d.progressive || {}), enabled: e.target.checked }; })}
                        />
                        Enabled
                    </label>
                    {[
                        ['seed', 'Seed ($)', 'number', 1],
                        ['reset', 'Reset Floor ($)', 'number', 1],
                        ['contribution_rate', 'Contribution Rate (0-1)', 'number', 0.001],
                        ['minimum_qualifying_bet', 'Min Qualifying Bet ($)', 'number', 0.05],
                        ['maximum_cap', 'Maximum Cap ($)', 'number', 100],
                    ].map(([key, label, type, step]) => (
                        <label key={key} className="text-sm">
                            <span className="block text-white/70 mb-1">{label}</span>
                            <input
                                type={type}
                                step={step}
                                value={draft.progressive?.[key] ?? 0}
                                onChange={e => patch(d => {
                                    d.progressive = { ...(d.progressive || {}), [key]: parseFloat(e.target.value) || 0 };
                                })}
                                className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                            />
                        </label>
                    ))}
                </div>
            </fieldset>

            {/* --- Paytable Grid --- */}
            <fieldset className="border border-white/10 rounded-lg p-3">
                <legend className="text-xs font-black tracking-widest text-yellow-400 px-2">PAYTABLE (spots × hits → multiplier)</legend>
                <div className="overflow-x-auto">
                    <table className="text-xs font-mono w-full">
                        <thead>
                            <tr>
                                <th className="p-1 text-yellow-400 text-right">HITS ↓ / SPOTS →</th>
                                {spots.map(s => <th key={s} className="p-1 text-yellow-400">{s}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {hitsRows.map(h => (
                                <tr key={h}>
                                    <td className="p-1 text-yellow-400 font-black text-right">{h}</td>
                                    {spots.map(s => {
                                        const cur = draft.paytable?.[String(s)]?.[String(h)]
                                                 ?? draft.paytable?.[s]?.[h]
                                                 ?? '';
                                        return (
                                            <td key={s} className="p-1">
                                                <input
                                                    type="number"
                                                    className="w-14 bg-black/60 border border-white/20 rounded px-1 py-0.5 text-center text-white"
                                                    value={cur}
                                                    onChange={e => patch(d => {
                                                        const v = parseFloat(e.target.value);
                                                        d.paytable = d.paytable || {};
                                                        d.paytable[String(s)] = d.paytable[String(s)] || {};
                                                        if (!e.target.value || isNaN(v)) delete d.paytable[String(s)][String(h)];
                                                        else d.paytable[String(s)][String(h)] = v;
                                                    })}
                                                    placeholder="—"
                                                    data-testid={`keno-pt-${s}-${h}`}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-white/50 mt-2">
                    Multiplier = payout ÷ bet. Leave blank for &quot;no win.&quot; Rows above the spot count don&apos;t apply
                    (you can&apos;t hit 8 on a 4-spot card).
                </p>
            </fieldset>

            {/* --- Timings --- */}
            <fieldset className="border border-white/10 rounded-lg p-3">
                <legend className="text-xs font-black tracking-widest text-yellow-400 px-2">REVEAL TIMINGS (ms)</legend>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(draft.timings || {}).map(([key, val]) => (
                        <label key={key} className="text-sm">
                            <span className="block text-white/70 mb-1 truncate">{key}</span>
                            <input
                                type="number"
                                min="0"
                                step="10"
                                value={val ?? 0}
                                onChange={e => patch(d => {
                                    d.timings = { ...(d.timings || {}), [key]: parseInt(e.target.value) || 0 };
                                })}
                                className="w-full bg-black/60 border border-white/20 rounded px-2 py-1"
                            />
                        </label>
                    ))}
                </div>
            </fieldset>
        </div>
    );
};

export default KenoConfigsAdminTab;
